import React, { useMemo, useState } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useCms } from "./CmsContext";
import { humanize, isHtmlKey, isImageKey, isImagePathValue, isLongText } from "./cmsUtils";
import { MediaPickerModal } from "./MediaPickerModal";

/* ------------------------------------------------------------------ */
/* primitives                                                          */
/* ------------------------------------------------------------------ */

export function TextField({ label, value, onChange, placeholder, hint }) {
  return (
    <div className="lw-field">
      {label && <label className="lw-field__label">{label}</label>}
      <input
        className="lw-input"
        type="text"
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="lw-field__hint">{hint}</p>}
    </div>
  );
}

export function TextAreaField({ label, value, onChange, rows = 3, hint }) {
  return (
    <div className="lw-field">
      {label && <label className="lw-field__label">{label}</label>}
      <textarea
        className="lw-input lw-textarea"
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className="lw-field__hint">{hint}</p>}
    </div>
  );
}

export function ToggleField({ label, value, onChange }) {
  return (
    <div className="lw-field lw-field--inline">
      <button
        type="button"
        role="switch"
        aria-checked={!!value}
        className={`lw-toggle ${value ? "lw-toggle--on" : ""}`}
        onClick={() => onChange(!value)}
      >
        <span className="lw-toggle__knob" />
      </button>
      {label && <label className="lw-field__label">{label}</label>}
    </div>
  );
}

export function NumberField({ label, value, onChange }) {
  return (
    <div className="lw-field">
      {label && <label className="lw-field__label">{label}</label>}
      <input
        className="lw-input lw-input--number"
        type="number"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      />
    </div>
  );
}

export function SelectField({ label, value, onChange, options, hint }) {
  return (
    <div className="lw-field">
      {label && <label className="lw-field__label">{label}</label>}
      <select className="lw-input lw-select" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {hint && <p className="lw-field__hint">{hint}</p>}
    </div>
  );
}

export function ImageField({ label, value, onChange, hint }) {
  const { config } = useCms();
  const [pickerOpen, setPickerOpen] = useState(false);
  const displaySrc =
    value && value.startsWith("/") ? `${config.siteUrl.replace(/\/$/, "")}${value}` : value;

  return (
    <div className="lw-field">
      {label && <label className="lw-field__label">{label}</label>}
      <div className="lw-imagefield">
        <div className="lw-imagefield__thumb">
          {value ? (
            <img src={displaySrc} alt="" loading="lazy" />
          ) : (
            <span className="lw-imagefield__empty">No image</span>
          )}
        </div>
        <div className="lw-imagefield__controls">
          <input
            className="lw-input"
            type="text"
            value={value ?? ""}
            placeholder="/media/… or https://…"
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="lw-imagefield__buttons">
            <button type="button" className="lw-btn lw-btn--secondary lw-btn--sm" onClick={() => setPickerOpen(true)}>
              Choose image
            </button>
            {value && (
              <button type="button" className="lw-btn lw-btn--ghost lw-btn--sm" onClick={() => onChange("")}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>
      {hint && <p className="lw-field__hint">{hint}</p>}
      {pickerOpen && (
        <MediaPickerModal
          onSelect={(url) => {
            onChange(url);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}

export function HtmlField({ label, value, onChange, hint }) {
  return (
    <div className="lw-field lw-field--html">
      {label && <label className="lw-field__label">{label}</label>}
      <div className="lw-richtext">
        <CKEditor
          editor={ClassicEditor}
          data={value ?? ""}
          config={{
            toolbar: [
              "heading",
              "|",
              "bold",
              "italic",
              "link",
              "bulletedList",
              "numberedList",
              "|",
              "blockQuote",
              "insertTable",
              "|",
              "undo",
              "redo",
            ],
          }}
          onChange={(event, editor) => onChange(editor.getData())}
        />
      </div>
      {hint && <p className="lw-field__hint">{hint}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* lists                                                               */
/* ------------------------------------------------------------------ */

function StringListEditor({ label, value, onChange, itemKey }) {
  const items = Array.isArray(value) ? value : [];
  const longItems = items.some((s) => typeof s === "string" && s.length > 90);
  return (
    <div className="lw-field lw-listfield">
      {label && <label className="lw-field__label">{label}</label>}
      <div className="lw-listfield__items">
        {items.map((item, i) => (
          <div key={i} className="lw-listfield__item">
            {longItems ? (
              <textarea
                className="lw-input lw-textarea"
                rows={3}
                value={item ?? ""}
                onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
              />
            ) : (
              <input
                className="lw-input"
                type="text"
                value={item ?? ""}
                onChange={(e) => onChange(items.map((v, j) => (j === i ? e.target.value : v)))}
              />
            )}
            <ListItemControls
              index={i}
              count={items.length}
              onMove={(from, to) => {
                const next = [...items];
                const [moved] = next.splice(from, 1);
                next.splice(to, 0, moved);
                onChange(next);
              }}
              onRemove={() => onChange(items.filter((_, j) => j !== i))}
            />
          </div>
        ))}
      </div>
      <button type="button" className="lw-btn lw-btn--ghost lw-btn--sm" onClick={() => onChange([...items, ""])}>
        + Add {humanize(itemKey || "item").toLowerCase()}
      </button>
    </div>
  );
}

function ListItemControls({ index, count, onMove, onRemove }) {
  return (
    <div className="lw-listfield__controls">
      <button
        type="button"
        className="lw-iconbtn"
        title="Move up"
        disabled={index === 0}
        onClick={() => onMove(index, index - 1)}
      >
        ↑
      </button>
      <button
        type="button"
        className="lw-iconbtn"
        title="Move down"
        disabled={index === count - 1}
        onClick={() => onMove(index, index + 1)}
      >
        ↓
      </button>
      <button type="button" className="lw-iconbtn lw-iconbtn--danger" title="Remove" onClick={onRemove}>
        ×
      </button>
    </div>
  );
}

// label used for a collapsed object list item
const objectItemLabel = (item, index) => {
  if (!item || typeof item !== "object") return `Item ${index + 1}`;
  const candidate =
    item.title || item.name || item.label || item.question || item.value || item.alt || item.attribution;
  if (typeof candidate === "string" && candidate.trim()) {
    return candidate.length > 60 ? `${candidate.slice(0, 60)}…` : candidate;
  }
  const firstString = Object.values(item).find((v) => typeof v === "string" && v.trim());
  if (firstString) return firstString.length > 60 ? `${firstString.slice(0, 60)}…` : firstString;
  return `Item ${index + 1}`;
};

// blank template based on an existing item
const blankFromTemplate = (template) => {
  if (template == null) return "";
  if (typeof template === "string") return "";
  if (typeof template === "number") return 0;
  if (typeof template === "boolean") return false;
  if (Array.isArray(template)) return [];
  const blank = {};
  for (const [k, v] of Object.entries(template)) {
    blank[k] = blankFromTemplate(v);
  }
  return blank;
};

function ObjectListEditor({ label, value, onChange, itemKey }) {
  const items = Array.isArray(value) ? value : [];
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="lw-field lw-objectlist">
      {label && <label className="lw-field__label">{label}</label>}
      <div className="lw-objectlist__items">
        {items.map((item, i) => (
          <div key={i} className={`lw-objectlist__item ${openIndex === i ? "lw-objectlist__item--open" : ""}`}>
            <div className="lw-objectlist__head">
              <button
                type="button"
                className="lw-objectlist__toggle"
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
              >
                <span className="lw-objectlist__chevron">{openIndex === i ? "▾" : "▸"}</span>
                <span className="lw-objectlist__title">{objectItemLabel(item, i)}</span>
              </button>
              <ListItemControls
                index={i}
                count={items.length}
                onMove={(from, to) => {
                  const next = [...items];
                  const [moved] = next.splice(from, 1);
                  next.splice(to, 0, moved);
                  onChange(next);
                  if (openIndex === from) setOpenIndex(to);
                }}
                onRemove={() => {
                  onChange(items.filter((_, j) => j !== i));
                  setOpenIndex(null);
                }}
              />
            </div>
            {openIndex === i && (
              <div className="lw-objectlist__body">
                <StructuredFields
                  value={item}
                  onChange={(next) => onChange(items.map((v, j) => (j === i ? next : v)))}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        className="lw-btn lw-btn--ghost lw-btn--sm"
        onClick={() => {
          const template = items.length > 0 ? blankFromTemplate(items[items.length - 1]) : {};
          onChange([...items, template]);
          setOpenIndex(items.length);
        }}
      >
        + Add {humanize(itemKey || "item").toLowerCase()}
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* recursive structured editor                                         */
/* ------------------------------------------------------------------ */

// keys managed elsewhere (never rendered as editable fields)
const HIDDEN_KEYS = new Set(["order", "id", "slug"]);

// singularize a list key for "+ Add x" labels
const singularKey = (key) => String(key).replace(/ies$/, "y").replace(/s$/, "");

export function FieldForValue({ fieldKey, value, onChange, siblings }) {
  const label = humanize(fieldKey);

  if (isHtmlKey(fieldKey)) {
    return <HtmlField label={label} value={value} onChange={onChange} />;
  }

  if (typeof value === "boolean") {
    return <ToggleField label={label} value={value} onChange={onChange} />;
  }

  if (typeof value === "number") {
    return <NumberField label={label} value={value} onChange={onChange} />;
  }

  if (Array.isArray(value)) {
    const allStrings = value.every((v) => typeof v === "string" || v == null);
    if (value.length === 0 || allStrings) {
      // empty arrays default to string lists (safe for bullets / paragraphs)
      const hasObjects = value.some((v) => v && typeof v === "object");
      if (!hasObjects) {
        return <StringListEditor label={label} value={value} onChange={onChange} itemKey={singularKey(fieldKey)} />;
      }
    }
    return <ObjectListEditor label={label} value={value} onChange={onChange} itemKey={singularKey(fieldKey)} />;
  }

  if (value !== null && typeof value === "object") {
    return (
      <div className="lw-group">
        <p className="lw-group__label">{label}</p>
        <div className="lw-group__body">
          <StructuredFields value={value} onChange={onChange} />
        </div>
      </div>
    );
  }

  // string / null
  if (isImageKey(fieldKey) || isImagePathValue(value)) {
    return <ImageField label={label} value={value ?? ""} onChange={(v) => onChange(v || null)} />;
  }
  if (isLongText(fieldKey, value)) {
    return (
      <TextAreaField
        label={label}
        value={value ?? ""}
        rows={Math.min(8, Math.max(3, Math.ceil(String(value ?? "").length / 100)))}
        onChange={(v) => onChange(v === "" && value === null ? null : v)}
      />
    );
  }
  return (
    <TextField
      label={label}
      value={value ?? ""}
      onChange={(v) => onChange(v === "" && value === null ? null : v)}
    />
  );
}

export function StructuredFields({ value, onChange }) {
  const entries = useMemo(() => Object.entries(value ?? {}), [value]);
  return (
    <div className="lw-fields">
      {entries
        .filter(([key]) => !HIDDEN_KEYS.has(key))
        .map(([key, fieldValue]) => (
          <FieldForValue
            key={key}
            fieldKey={key}
            value={fieldValue}
            siblings={value}
            onChange={(next) => onChange({ ...value, [key]: next })}
          />
        ))}
    </div>
  );
}
